import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { downloadApiExport, getDateForFileName, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

const BASE_URL = import.meta.env.REACT_APP_API_ENDPOINT + "/title/format-types";

const toastFromPayload = (payload: unknown) => {
    const message =
        payload && typeof payload === "object" && "message" in payload
            ? String((payload as { message?: string }).message)
            : undefined;
    toast.error(translationsWithFallback(message ?? "", "Settings.FormatTypeError"));
};

export type ITitleFormatType = {
    id: number;
    name: string;
    companyId: number;
};

export const fetchTitleFormatTypes = createAsyncThunk("titleFormatTypes/fetchAll", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(BASE_URL);
        return response.data.data as ITitleFormatType[];
    } catch (error: any) {
        return rejectWithValue(error.response?.data);
    }
});

export const createTitleFormatType = createAsyncThunk(
    "titleFormatTypes/create",
    async ({ name }: { name: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(BASE_URL, { name });
            return response.data.data as ITitleFormatType;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const updateTitleFormatType = createAsyncThunk(
    "titleFormatTypes/update",
    async ({ id, name }: { id: number; name: string }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${BASE_URL}/${id}`, { name });
            return response.data.data as ITitleFormatType;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const exportTitleFormatTypeList = createAsyncThunk("titleFormatTypes/export", async () => {
    const response = await toast.promise(
        axios.get(`${BASE_URL}/export`, {
            headers: { Accept: "*/*" },
        }),
        {
            pending: `${t("Export.Pending")}`,
            success: `${t("Export.Successful")}`,
            error: `${t("Export.Error")}`,
        },
    );

    const fileName = `export_format_types_${getDateForFileName()}.xlsx`;
    downloadApiExport(response.data, fileName);
});

export const deleteTitleFormatType = createAsyncThunk(
    "titleFormatTypes/delete",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            await axios.delete(`${BASE_URL}/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

type TitleFormatTypeState = {
    items: ITitleFormatType[];
    status: TFetchStatus;
};

const initialState: TitleFormatTypeState = {
    items: [],
    status: "idle",
};

const titleFormatTypeSlice = createSlice({
    name: "titleFormatTypes",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchTitleFormatTypes.pending, (state) => {
            state.status = "pending";
        });
        builder.addCase(fetchTitleFormatTypes.fulfilled, (state, action) => {
            state.status = "succeeded";
            state.items = action.payload;
        });
        builder.addCase(fetchTitleFormatTypes.rejected, (state) => {
            state.status = "failed";
        });
        builder.addCase(createTitleFormatType.fulfilled, (state, action) => {
            state.items = [...state.items, action.payload].sort((a, b) => a.name.localeCompare(b.name));
            toast.success(`${t("Settings.FormatTypeCreated")}`);
        });
        builder.addCase(createTitleFormatType.rejected, (_state, { payload }) => {
            toastFromPayload(payload);
        });
        builder.addCase(updateTitleFormatType.fulfilled, (state, action) => {
            state.items = state.items.map((item) => (item.id === action.payload.id ? action.payload : item));
            toast.success(`${t("Settings.FormatTypeUpdated")}`);
        });
        builder.addCase(updateTitleFormatType.rejected, (_state, { payload }) => {
            toastFromPayload(payload);
        });
        builder.addCase(deleteTitleFormatType.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.id !== action.payload);
            toast.success(`${t("Settings.FormatTypeDeleted")}`);
        });
        builder.addCase(deleteTitleFormatType.rejected, (_state, { payload }) => {
            toastFromPayload(payload);
        });
    },
});

export default titleFormatTypeSlice.reducer;
